import {DEVICE_TYPES} from '@/consts/device/deviceTypes';
import devicePlatformService from '@/services/device/devicePlatformService';
import deviceTypesService from '@/services/device/deviceTypesService';
import marketConfigService from '@/services/marketConfigService';

const getVariableName = (variableObj) => Object.keys(variableObj)[0];

const getIdentificatorObject = (variableObj) => {
    const identificatorName = getVariableName(variableObj);
    const identificatorValue = variableObj[identificatorName];

    return {
        identificatorName,
        identificatorValue,
    };
};

const getProductIdentificator = (product) => {
    const {codentify, deviceSerialNumber} = product || {};
    const identificatorObject = deviceSerialNumber
        ? getIdentificatorObject({deviceSerialNumber})
        : getIdentificatorObject({codentify});

    return identificatorObject;
};

const getValidProducts = (products) => {
    return products?.filter((x) => !!x) || [];
};

const someProduct = ({products, deviceSerialNumber, codentify}) => {
    const {identificatorName, identificatorValue} = getProductIdentificator({
        deviceSerialNumber,
        codentify,
    });

    return getValidProducts(products).some((product) => product[identificatorName] === identificatorValue);
};

const findProduct = ({products, deviceSerialNumber, codentify}) => {
    const {identificatorName, identificatorValue} = getProductIdentificator({
        deviceSerialNumber,
        codentify,
    });

    return getValidProducts(products).find((product) => product[identificatorName] === identificatorValue);
};

const getNewProductsListWithUpdatedItem = ({products, deviceSerialNumber, codentify, data}) => {
    const isProductExist = someProduct({
        products,
        deviceSerialNumber,
        codentify,
    });

    let newList = products?.length ? [...products] : [];

    if (isProductExist) {
        newList = newList.map((product) => {
            if (product.deviceSerialNumber === deviceSerialNumber || product.codentify === codentify) {
                return {...product, ...data};
            }

            return product;
        });
    } else {
        newList.push({
            deviceSerialNumber,
            codentify,
            ...data,
        });
    }

    return newList;
};

const getTransformedDeviceSerialNumber = (deviceSerialNumber) => {
    if (deviceSerialNumber) {
        const upperCaseSerialNumber = deviceSerialNumber.toUpperCase();
        const isDusnFormat = upperCaseSerialNumber.startsWith('0X');

        if (isDusnFormat) {
            const tranformedSerialNumber =
                '0' + upperCaseSerialNumber.charAt(1).toLowerCase() + upperCaseSerialNumber.slice(2);

            return tranformedSerialNumber;
        }
    }

    return deviceSerialNumber;
};

const isOriginalCodentifyValid = (codentify) => {
    return codentify && !codentify.includes('*');
};

const promoteProductByCodentify = ({products, codentify}) => {
    const index = products.findIndex((product) => product.codentify === codentify);

    if (index !== -1) {
        const [product] = products.splice(index, 1);

        products.unshift(product);
    }

    return products;
};

const getProductName = (device) => {
    const {productName, defaultProductName} = device || {};
    const name = productName || defaultProductName || '';

    return name;
};

const getPreparedType = (material, deviceSerialNumber) => {
    const type = devicePlatformService.getDeviceTypeBySerialNumber(deviceSerialNumber);
    const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({
        material: {materialBrandFamily: material?.materialBrandFamily},
    });
    let preparedType = type;

    if (DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER === type && isOriginalDuoDevice) {
        preparedType = DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
    } else if (DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER === type && isOriginalDuoDevice) {
        preparedType = DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER;
    }

    return preparedType;
};

const getMediaName = (material, isP1V41) => {
    const color = isP1V41 ? material.materialBrandDifferentiator || material.materialColor : material.materialColor;
    const mediaIdParts = [material.materialGroup, material.materialBrandFamily, material.materialModelNumber, color];

    return mediaIdParts.join('_');
};

const mapDeviceData = (product, products, productsInfo, connectedHolderCodentify, isConnectedDevice) => {
    const {codentify, deviceSerialNumber, parentCodentifyID, parentProductID, defaultHolderType} = product;
    const productInfo = productsInfo?.find((productInfo) => productInfo.deviceSerialNumber === deviceSerialNumber);

    const {codentifyID, colorHex, mediaId, defaultProductName, type} = productInfo || {};

    if (!codentify && !codentifyID) return null;

    const productType = type ? type : devicePlatformService.getDeviceTypeBySerialNumber(deviceSerialNumber);
    const defaultNotConnectedHolderType = productInfo?.defaultHolderType
        ? productInfo?.defaultHolderType
        : defaultHolderType;
    const defaultProductHolderType = isConnectedDevice ? undefined : defaultNotConnectedHolderType;

    let mergedHolderInfo;

    if (isConnectedDevice) {
        const holder = findProduct({products, codentify: connectedHolderCodentify});

        mergedHolderInfo = mapHolderData(productsInfo, holder);
    } else if (parentCodentifyID || parentProductID) {
        const holders = products.filter((product) => product.isHolder);
        const holder = holders.find(
            (h) =>
                (parentCodentifyID && h.parentCodentifyID === parentCodentifyID) ||
                (parentProductID && h.parentProductID === parentProductID)
        );

        mergedHolderInfo = mapHolderData(productsInfo, holder);
    }

    return {
        ...product,
        colorHex,
        mediaId,
        defaultProductName: defaultProductName
            ? defaultProductName
            : deviceTypesService.getProductNameByType(productType),
        defaultHolderType: defaultProductHolderType,
        isDeviceSupported: marketConfigService.isDeviceSupported(productType),
        type: productType,
        holder: mergedHolderInfo,
    };
};

const mapHolderData = (productsInfo, holder) => {
    if (!holder?.deviceSerialNumber) return;

    const holderInfo = productsInfo?.find(
        (productInfo) => productInfo.deviceSerialNumber === holder.deviceSerialNumber
    );

    const {colorHex, mediaId, defaultProductName, type} = holderInfo || {};

    const holderType = type ? type : devicePlatformService.getDeviceTypeBySerialNumber(holder.deviceSerialNumber);

    return {
        ...holder,
        colorHex,
        mediaId,
        defaultProductName: defaultProductName
            ? defaultProductName
            : deviceTypesService.getProductNameByType(holderType),
        type: holderType,
    };
};

export default {
    getProductName,
    findProduct,
    getNewProductsListWithUpdatedItem,
    getTransformedDeviceSerialNumber,
    isOriginalCodentifyValid,
    promoteProductByCodentify,
    getPreparedType,
    getMediaName,
    mapDeviceData,
};
